/**
 * Dárek zdarma
 * ---------------------------------------------------------------
 * Sleduje obsah košíku přes theme eventy a:
 *  - když uživatel klikne na „Přejít k pokladně“ (v cart draweru i na
 *    stránce košíku) a košík splňuje limit bez dárku, zadrží přechod
 *    a otevře popup s výběrem příchutě,
 *  - v popupu si vybere: „Přidat dárek a pokračovat“ (přidá variantu
 *    s line property `_free_gift` a přejde k pokladně) nebo
 *    „Pokračovat bez dárku“ (jen přejde k pokladně),
 *  - po zvolení „bez dárku“ už v rámci návštěvy (session) neotravuje,
 *  - k pokladně nepustí bez doručení zvoleného ve widgetu Globe (vrátí klik
 *    tlačítku, aby Globe ukázal svou hlášku),
 *  - když košík klesne pod limit, dárek automaticky odebere.
 *
 * Vzhled, backdrop, animace otevírání/zavírání, Esc, focus-trap
 * i zámek scrollu dědíme z nativní theme komponenty <basic-modal>
 * (centrovaný modal). Tady řešíme jen logiku košíku.
 *
 * Samotnou cenu dárku (= 0) řeší automatická sleva v Shopify adminu
 * (Buy X Get Y). Tento skript cenu vůbec neřeší – jen přidává/odebírá
 * produkt.
 */
customElements.whenDefined('basic-modal').then(() => {
  const BasicModal = customElements.get('basic-modal');

  class FreeGift extends BasicModal {
    constructor() {
      super();

      this.threshold = parseInt(this.dataset.threshold, 10) || 0;
      this.variantIds = (this.dataset.variantIds || '')
        .split(',')
        .map((id) => parseInt(id.trim(), 10))
        .filter(Boolean);

      this.busy = false; // probíhá fetch na košík – blokuje souběžné akce
      this.systemClose = false; // true = zavírá systém (nezapamatovat)
      this.sessionKey = 'free-gift-dismissed';
    }

    connectedCallback() {
      // Modal lifecycle (controls, Esc, focus-trap) – musí běžet po každém
      // připojení do DOM, protože komponenta se při otevření přesouvá do <body>.
      super.connectedCallback();

      if (!this.variantIds.length || !this.threshold) return;

      // Cart logiku navážeme jen jednou – listenery na document i na vnitřní
      // prvky přežijí přesun elementu do <body>.
      if (this.initialized) return;
      this.initialized = true;

      this.select = this.querySelector('[data-free-gift-select]');
      this.addButton = this.querySelector('[data-free-gift-add]');
      this.skipButton = this.querySelector('[data-free-gift-skip]');

      if (this.addButton) {
        this.addButton.addEventListener('click', () => this.addGift());
      }
      if (this.skipButton) {
        this.skipButton.addEventListener('click', () => this.skipGift());
      }

      // Napojení na cart eventy tématu – jen držíme aktuální stav košíku
      // a hlídáme pokles pod limit (odebrání dárku). Popup už neotevíráme
      // automaticky, ale až při pokusu o přechod k pokladně.
      this.cartUpdatedListener = (event) => this.evaluate(event.detail.cart);
      document.addEventListener('cart:updated', this.cartUpdatedListener);

      // Zachytíme kliknutí na „Přejít k pokladně“ v capture fázi (dřív, než
      // formulář košíku odešle), abychom stihli nabídnout dárek. Delegujeme na
      // document, protože cart drawer se při změnách překresluje.
      this.checkoutClickListener = (event) => this.onCheckoutClick(event);
      document.addEventListener('click', this.checkoutClickListener, true);

      // Výchozí stav podle aktuálního košíku
      this.fetchCart().then((cart) => cart && this.evaluate(cart));
    }

    /** Po otevření fokusujeme roletku příchutě, ne křížek. */
    get focusElement() {
      return this.select || super.focusElement;
    }

    get dismissed() {
      return sessionStorage.getItem(this.sessionKey) === 'true';
    }

    set dismissed(value) {
      if (value) {
        sessionStorage.setItem(this.sessionKey, 'true');
      } else {
        sessionStorage.removeItem(this.sessionKey);
      }
    }

    /**
     * Zavření iniciované systémem (dosažení/ztráta limitu, přidání dárku).
     * Na rozdíl od ručního zavření si ho nepamatujeme.
     */
    systemHide() {
      this.systemClose = true;
      this.hide();
      this.systemClose = false;
    }

    /**
     * Override – uživatelské zavření (overlay, křížek, Esc) si zapamatujeme,
     * aby se nabídka nevracela dokola. `hide()` odebírá atribut synchronně,
     * takže příznak stihneme vyhodnotit dřív, než ho `systemHide` vrátí zpět.
     */
    hide() {
      if (this.open && !this.systemClose) this.dismissed = true;
      return super.hide();
    }

    /**
     * Vyhodnotí stav košíku. Popup se tu už NEotevírá – jen si držíme
     * aktuální stav a hlídáme pokles pod limit (kdy dárek odebereme).
     * @param {object} cart – Shopify cart JSON (z eventu nebo /cart.js)
     */
    evaluate(cart) {
      if (!cart || !Array.isArray(cart.items) || this.busy) return;

      this.lastCart = cart;

      const giftLine = this.findGiftLine(cart);

      if (this.qualifyingTotal(cart) < this.threshold) {
        // Pod limitem – dárek pryč a reset, aby šel po opětovném dosažení znovu nabídnout
        this.dismissed = false;
        if (giftLine) this.removeGift(giftLine.key);
        this.systemHide();
        return;
      }

      // Nad limitem a dárek už v košíku – pokud byl popup otevřený, zavřeme ho.
      if (giftLine) this.systemHide();
    }

    /** Součet položek, které NEjsou dárek (sladěno s podmínkou slevy). */
    qualifyingTotal(cart) {
      const giftLine = this.findGiftLine(cart);
      return cart.items
        .filter((item) => item !== giftLine)
        .reduce((sum, item) => sum + item.final_line_price, 0);
    }

    /** Má při pokusu o checkout vyskočit nabídka dárku? */
    shouldOfferGift() {
      const cart = this.lastCart;
      if (!cart || !Array.isArray(cart.items)) return false;
      if (this.dismissed) return false; // už zvolil „bez dárku“ (jen jednou za session)
      if (this.findGiftLine(cart)) return false; // dárek už má
      return this.qualifyingTotal(cart) >= this.threshold;
    }

    /**
     * Kliknutí na „Přejít k pokladně“. Pokud košík splňuje limit bez dárku
     * a uživatel nabídku ještě neodmítl, zadržíme odeslání a nabídneme dárek.
     */
    onCheckoutClick(event) {
      const button = event.target.closest && event.target.closest('button[name="checkout"]');
      if (!button || !this.shouldOfferGift()) return;

      event.preventDefault();
      event.stopPropagation();

      this.checkoutButton = button;
      this.show();
    }

    /** „Pokračovat bez dárku“ – zapamatujeme volbu a jdeme k pokladně. */
    skipGift() {
      this.dismissed = true;
      this.proceedToCheckout(false);
    }

    /**
     * Přesměruje na pokladnu (respektuje případný locale prefix). Klik na
     * „Přejít k pokladně“ jsme zadrželi dřív než widget Globe, takže když
     * v košíku chybí zvolené doručení, vrátíme klik tlačítku – Globe ukáže
     * svou hlášku a k pokladně nepustí. Po přidání dárku místo toho jen
     * překreslíme košík, aby byl dárek vidět.
     * @param {boolean} giftAdded – dárek se právě přidal do košíku
     */
    proceedToCheckout(giftAdded) {
      this.deliveryMissing().then((missing) => {
        if (missing) {
          this.setLoading(false);
          this.systemHide();
          if (giftAdded) {
            // Stránka košíku se na `cart:refresh` nepřekresluje, jen cart drawer.
            if (document.querySelector('main-cart')) {
              window.location.reload();
            } else {
              this.refreshCart(false);
            }
          } else if (this.checkoutButton && this.checkoutButton.isConnected) {
            this.checkoutButton.click();
          }
          return;
        }

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        window.location.href = `${root}checkout`.replace(/\/{2,}/g, '/');
      });
    }

    /** Je na stránce výběr doručení Globe a v košíku přitom chybí zvolené doručení? */
    deliveryMissing() {
      if (!document.querySelector('.globe-delivery-selector-container')) return Promise.resolve(false);

      return this.fetchCart().then((cart) => {
        if (!cart) return false;
        this.lastCart = cart;
        const attributes = cart.attributes || {};
        const method = attributes.globe_DeliveryMethod;
        return !method || (method === 'pickup' && !attributes.globe_PickupPointId);
      });
    }

    /** Najde v košíku řádek s dárkem (podle line property nebo ID varianty). */
    findGiftLine(cart) {
      return cart.items.find((item) => {
        const isFlagged = item.properties && item.properties._free_gift;
        const isGiftVariant = this.variantIds.includes(item.variant_id || item.id);
        return isFlagged || isGiftVariant;
      });
    }

    /** Přidá vybranou variantu dárku do košíku. */
    addGift() {
      if (this.busy || !this.select || !this.select.value) return;

      this.setLoading(true);

      const body = JSON.stringify({
        items: [
          {
            id: parseInt(this.select.value, 10),
            quantity: 1,
            properties: { _free_gift: 'true' },
          },
        ],
      });

      fetch(`${window.FoxTheme.routes.cart_add_url}`, {
        ...window.FoxTheme.utils.fetchConfig(),
        body,
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.status) {
            // Chyba (např. nedostupná varianta) – nech popup otevřený
            console.warn('Free gift add error:', data.description || data.message);
            this.setLoading(false);
            return;
          }
          // Dárek je v košíku → rovnou k pokladně (spinner necháme až do redirectu).
          this.proceedToCheckout(true);
        })
        .catch((error) => {
          console.error('Free gift add failed:', error);
          this.setLoading(false);
        });
    }

    /** Odebere dárek z košíku podle line key. */
    removeGift(lineKey) {
      if (this.busy) return;
      this.busy = true;

      const body = JSON.stringify({ id: lineKey, quantity: 0 });

      fetch(`${window.FoxTheme.routes.cart_change_url}`, {
        ...window.FoxTheme.utils.fetchConfig(),
        body,
      })
        .then((response) => response.json())
        .then(() => this.refreshCart(false))
        .catch((error) => console.error('Free gift remove failed:', error))
        .finally(() => {
          this.busy = false;
        });
    }

    /** Stáhne aktuální stav košíku. */
    fetchCart() {
      return fetch(`${window.FoxTheme.routes.cart_url}.js`)
        .then((response) => response.json())
        .catch(() => null);
    }

    /**
     * Přerenderuje cart drawer pomocí theme eventu. CartDrawer si sám
     * stáhne data a publikuje `cart:updated`, takže se stav dorovná.
     */
    refreshCart(openDrawer) {
      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { open: openDrawer === true },
        })
      );
    }

    setLoading(isLoading) {
      this.busy = isLoading;
      this.addButton.classList.toggle('btn--loading', isLoading);
      this.addButton.disabled = isLoading;
    }
  }

  customElements.define('free-gift', FreeGift);
});
